import { useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { endOfWeek, startOfWeek } from 'date-fns';
import { useEffectiveChildMode } from '@/components/utils/useEffectiveChildMode';
import { EmbeddedCompanion, useCompanionMoment } from '@/components/character';
import { useChildTokens } from '@/components/child';
import { dateLocales } from '@/i18n/dateLocales';
import WeekView, { WeekViewFlags } from '@/components/calendar/weekly/WeekView';
import WeekNavLabel from '@/components/calendar/weekly/WeekNavLabel';
import { childToWeekTokens } from '@/components/calendar/weekly/weekTokens';

/**
 * ChildWeek — tunn wrapper runt den gemensamma WeekView. Barnvyns lekfullhet
 * (alltid öppna dagar, EmbeddedCompanion-maskot, emoji-ikoner) läggs på via
 * flaggor. Färgerna tas oförändrade ur barntemat (palette.child) och mappas till
 * det gemensamma token-kontraktet (childToWeekTokens) — inga synliga färgändringar.
 * Barn/teen är låsta till innevarande vecka (statisk WeekNavLabel, ingen bläddring).
 */
const CHILD_FLAGS: WeekViewFlags = {
  collapsibleDays: false,
  showDayCount: false,
  showWeekProgress: false,
  showDayStar: false,
  showStatusDots: false,
  showActivityEmoji: true,
  showCompanion: true,
  todayGlow: false,
};

const ChildWeekInner = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const childTokens = useChildTokens();

  useCompanionMoment('idle', 'child.companion.week');

  const locale = dateLocales[i18n.language.split('-')[0]] ?? dateLocales.en;
  const tokens = childToWeekTokens(
    childTokens,
    theme.palette.primary.main,
    theme.palette.success.main,
  );

  const weekStart = startOfWeek(new Date(), { weekStartsOn: 1 });
  const weekEnd = endOfWeek(new Date(), { weekStartsOn: 1 });

  return (
    <WeekView
      title={t('child.week.title')}
      weekStart={weekStart}
      tokens={tokens}
      flags={CHILD_FLAGS}
      weekNav={<WeekNavLabel weekStart={weekStart} weekEnd={weekEnd} tokens={tokens} />}
      companion={<EmbeddedCompanion variant="row" accent="green" />}
      todayLabel={t('week.today')}
      noActivitiesLabel={t('child.week.noActivities')}
      weekProgressLabel=""
      nowLabel={t('week.now')}
      locale={locale}
    />
  );
};

const ChildWeek = () => {
  const effectiveChildMode = useEffectiveChildMode();
  if (!effectiveChildMode) {
    return null;
  }
  return <ChildWeekInner />;
};

export default ChildWeek;
